import * as React from 'react';
import { Slider } from 'wix-style-react-tpa';
import { TPAComponentsContext } from '../TPAComponentsConfig';
import { st, classes } from './Range.st.css';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import { RangeProps, RangeDataHooks } from './types';
import uniqueId from '../../common/uniqueId';
import { shellClasses } from '../../common/shellClasses';

const formatValue = (value: number, format: RangeProps['format']): string => {
  const { locale = 'en', ...formatOptions } = format ?? {};
  return Intl.NumberFormat(locale, formatOptions as any).format(value);
};

export const Range: React.FC<RangeProps> = ({
  value,
  min = 0,
  max,
  format,
  label,
  disabled,
  minAriaLabel,
  maxAriaLabel,
  onChange,
  onAfterChange,
  errorMessage,
  errorAppearance,
  ...other
}) => {
  const sliderId = React.useMemo(() => uniqueId('slider-id_'), []);
  const errorMessageId = React.useMemo(() => uniqueId('error-message'), []);

  const { rtl } = React.useContext(TPAComponentsContext);

  const changeHandler = React.useCallback(
    (range: number[]) => {
      onChange && onChange(range);
    },
    [onChange],
  );

  const stStates = { error: !!errorMessage };

  return (
    <ErrorMessageWrapper
      {...{
        visible: !!errorMessage,
        message: errorMessage,
        errorAppearance,
        messageId: errorMessageId,
        className: st(classes.root, stStates, other.className),
        'data-hook': other['data-hook'],
      }}
    >
      <fieldset className={classes.container}>
        {label && (
          <legend data-hook={RangeDataHooks.Label} className={classes.label}>
            {label}
          </legend>
        )}

        <div className={classes.sliderWrapper}>
          <Slider
            id={sliderId}
            className={st(classes.slider, { rtl }, shellClasses.hasCustomFocus)}
            onChange={changeHandler}
            onAfterChange={onAfterChange}
            min={min}
            max={max}
            value={value || [min, max]}
            displayMarks={false}
            disabled={disabled}
            displayTooltip={false}
            rtl={rtl}
            ariaLabelForHandle={[minAriaLabel, maxAriaLabel]}
          />
        </div>

        <div className={classes.rangeValues}>
          <span data-hook={RangeDataHooks.SelectedMinValue}>
            {formatValue(value ? value[0] : min, format)}
          </span>
          <span data-hook={RangeDataHooks.SelectedMaxValue}>
            {formatValue(value ? value[1] : max, format)}
          </span>
        </div>
      </fieldset>
    </ErrorMessageWrapper>
  );
};

Range.displayName = 'Range';
